// CheckoutForm.js
import React, { useState, useEffect } from 'react'
import { print } from 'graphql'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import axios from 'axios'
import isEmpty from 'lodash.isempty'
import { useMutation } from '@apollo/react-hooks'
import { Link, navigate } from 'gatsby'
import tw, { css, styled } from 'twin.macro'
import { Global } from '@emotion/react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Menu, MenuList, MenuButton, MenuItem } from '@reach/menu-button'
/** Hooks */
import useCart from '../../hooks/useCart'
/** Queries */
import { CREATE_ORDER, PAY_ORDER } from '../../app/schema/order'
/** Page components */
import CheckoutPayment from './CheckoutPayment'
import CheckoutShipping from './CheckoutShipping'
/** load Stripe */
const stripe = loadStripe(process.env.GATSBY_STRIPE_PUBLISH_KEY)
/** isBrowser */
const isBrowser = typeof window !== 'undefined'
/** SVG's */
import EMVChip from '../svg/emvChip'
import EMVContactless from '../svg/emvContactless'
import Visa from '../svg/visa'
import MasterCard from '../svg/masterCard'
import Amex from '../svg/amex'
/** Styles with Emotion & Tailwindcss */
import QueryString from 'qs'
	; import CheckoutExpress from './CheckoutExpress'
() => (
	<Global
		styles={css`
			.quantity-button[data-reach-menu-button] {
				${tw`inline-flex items-center`}
				${tw`border border-gray-600 rounded bg-gray-100 text-gray-600 text-xs py-1 px-1 leading-none`}
				outline: 0;
				&:hover,
				&:focus {
					outline: 0;
					box-shadow: 0 0 0 3px rgba(88, 200, 223, 0.85);
				}
				span {
					${tw`pl-2 pr-1 font-semibold`}
				}
			}
			.quantity-options[data-reach-menu-list] {
				${tw`border border-gray-600 rounded bg-gray-100 text-gray-600 text-sm py-2 px-1 my-2 shadow-lg`}
				outline: 0;
				&:hover,
				&:focus {
					outline: 0;
					box-shadow: 0 0 0 3px rgba(88, 200, 223, 0.85);
				}
				[data-reach-menu-item] {
					${tw`rounded-sm py-px px-0 hover:bg-gray-50 cursor-pointer`}
					outline: 0;
					&:hover,
					&:focus {
						outline: 0;
						box-shadow: 0 0 0 3px rgba(88, 200, 223, 0.85);
					}
				}
			}
		`}
	/>
)
const Align = styled.div`
	${tw`flex flex-col justify-start items-start`}
`
const StepWrapper = styled.div`
	${tw`w-full`}
	${tw`bg-white shadow overflow-hidden sm:rounded-md`}
`
const ItemWrapper = styled.div`
	${tw`block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out w-full`}
`
const SummeryWrapper = styled(StepWrapper)`
	${tw`w-full`}
	${tw`flex flex-col mt-4`}
`
const Item = tw.div`flex flex-col items-center px-4 py-4 sm:px-6`
const Summery = styled(Item)`
	${tw``}
`
const Content = styled.div`
	${tw`min-w-0 w-full flex-1 flex items-center`}
	.shipping-details {
		${tw`min-w-0 flex-1 px-4 grid grid-cols-1 lg:grid-cols-2 gap-4`}
	}
	.shipping-address {
		${tw`block ml-auto`}
	}
	.product-details {
		${tw`min-w-0 flex-1 px-4 grid grid-cols-2 gap-4`}
	}
	.product-selectors {
		${tw`block ml-auto`}
	}
`
const Price = tw.div`font-semibold text-gray-600`
const Heading1 = tw.h1`text-gray-800 text-4xl font-semibold font-display mb-2 md:mb-4 pt-4 md:pt-8`
const EmptyCart = tw.div`flex flex-col justify-start items-center w-full my-4 text-gray-600`
const EmptyMessage = tw.div`flex justify-center mb-2`
const EmptyButton = styled(Link)`
	${tw`flex bg-yellow-500 hover:bg-yellow-400 font-display text-black text-base px-4 py-1 rounded`}
`
const Heading2 = tw.h2`font-display text-lg mb-4`
const Button = styled.button`
	${tw`flex bg-yellow-500 hover:bg-yellow-400 text-gray-700 text-base px-4 py-2 rounded`}
	${({ hidden }) => (hidden ? tw`hidden` : ``)}
	${({ disabled }) =>
		disabled
			? tw`bg-yellow-200 hover:bg-yellow-200 text-gray-400 cursor-not-allowed`
			: ``}
`
const Card = styled.div`
	${tw`flex flex-col justify-start items-start bg-white rounded w-full my-2 px-2 py-4 shadow`}
	&#shipping {
		.shipping {
			${tw`flex mb-2`}
			.shipping-details {
				${tw`flex flex-col`}
			}
			.shipping-address {
				${tw`flex flex-col pl-2`}
			}
		}
	}
	&#billing {
		.billing {
			${tw`flex`}
			.billing-details {
				${tw`flex flex-col`}
			}
			.billing-address {
				${tw`flex flex-col`}
			}
		}
	}
`
const PaymentElement = styled.div`
	${tw`flex flex-col w-full`}
	${({ hidden }) => (hidden ? tw`hidden` : ``)}
`
const CreditCard = styled.div`
	${tw`pt-10 pb-2 bg-gray-300 rounded shadow relative mb-2 border-b-2 border-r-2 border-gray-400`}
	background: linear-gradient(270deg, rgba(218,225,231,1) 0%, rgba(135,149,161,1) 100%);
	figure {
		${tw`bg-yellow-300 w-10 rounded ml-8 mb-2 p-1`}
		> svg#emv-chip {
			${tw`m-0 w-8 text-gray-500`}
		}
	}
	small {
		${tw`rounded-full bg-gray-500 w-12 text-sm h-3 mt-auto ml-8`}
		&:last-of-type {
			${tw`ml-2 w-20`}
		}
	}
	span {
		${tw`text-gray-300 mb-2 px-8 font-mono`}
		text-shadow: 0 1px 0 #8795a1,
               0 0 5px rgba(135,149,161,.5),
               0 1px 3px rgba(135,149,161,.5),
               0 3px 5px rgba(135,149,161,.6),
               0 5px 10px rgba(135,149,161,.5),
               0 10px 10px rgba(135,149,161,.6);
		> i {
			${tw`not-italic text-gray-200`}
		}
	}
	svg {
		${tw`w-8 text-gray-500`}
		&#cc-visa, &#cc-mastercard, &#cc-amex {
			${tw`ml-auto mr-2`}
		}
		&#emv-contactless {
			${tw`ml-4 mb-2 p-1 w-8`}
		}
	}
`
const ShippingElement = styled.div`
	${tw`flex flex-col w-full`}
	${({ hidden }) => (hidden ? tw`hidden` : ``)}
`
const BillingElement = styled.div`
	${tw`flex flex-col w-full`}
	${({ hidden }) => (hidden ? tw`hidden` : ``)}
`
const Review = tw.div`flex flex-col w-full md:w-2/3 md:mr-2`
const Charges = styled.div`
	${tw`flex justify-end w-full`}
	${({ hidden }) => (hidden ? tw`hidden` : ``)}
`
const ChargeItem = styled.div`
	${tw`flex w-full justify-between content-center py-1 px-1 text-sm text-gray-600`}
	${({ disabled }) => (disabled ? tw`text-gray-400` : ``)}
  min-width: 270px;
`
const ChargeTitle = tw.div`flex flex-1`
const ChargeCost = tw.div`pl-1`
const Total = styled.div`
	${tw`flex w-full justify-between content-center py-4 px-2`}
	b {
		${tw`text-xl font-bold`}
	}
	${({ disabled }) => (disabled ? tw`opacity-50` : ``)}
`
const PlaceOrder = styled.button`
	${tw`flex w-full justify-center items-center`}
	${tw`bg-yellow-500 text-gray-700 px-4 py-2 text-lg rounded text-center`}
	&[disabled] {
		${tw`bg-yellow-200 text-gray-400 cursor-not-allowed`}
	}
	span {
		${tw`pl-2 pt-1 text-lg`}
	}
`

const CheckoutForm = props => {
	const {
		count,
		items,
		coupon,
		id,
		initCheckout,
		updateQuantity,
		incrementInCart,
		decrementInCart,
		removeFromCart,
		// addCoupon,
		couponTotal,
		subtotal,
		freight,
		isPickUp,
		tax,
		total,
		fee,
		orderTotal,
	} = useCart()
	const [isInput, setIsInput] = useState(items.map(() => false))
	const [couponInput, setCouponInput] = useState({ coupon: '' })
	// const handleOnChangeCouponInput = event =>
	// 	setCouponInput({ ...couponInput, [event.target.name]: event.target.value })
	// const handleOnSubmitCouponInput = async event => {
	// 	event.preventDefault()
	// 	addCoupon(couponInput.coupon)
	// 	return setCouponInput({ coupon: '' })
	// }

	const showInput = i => {
		const x = isInput.map((x, index) => (index === i ? true : false))
		return setIsInput(x)
	}
	const [sameAsShipping, setSameAsShipping] = useState(true)
	const [isLoading, setIsLoading] = useState(false)
	const [isShippingOpen, setIsShippingOpen] = useState(false)
	const [isPaymentOpen, setIsPaymentOpen] = useState(false)
	const [confirm, setConfirm] = useState(false)
	const [payment, setPayment] = useState({})
	const [billing, setBilling] = useState({})
	const [shipping, setShipping] = useState({})
	const [createOrder, order] = useMutation(CREATE_ORDER)
	const [optIn, setOptIn] = useState(true)
	const products = items =>
		items.map(({ name, sku, price, quantity }) => ({
			name,
			sku,
			price: Math.round(price),
			quantity,
		}))
	useEffect(() => {
		let isFinished = false
		const product = products(items)
		if (id) {
			return () => {
				isFinished = true
			}
		}
		createOrder({
			variables: {
				product,
				freight: [{ name: 'Freight', sku: 'FR', price: freight, quantity: 1 }],
				subtotal,
				tax,
				total,
			},
		})
			.then(res => initCheckout(res.data.createOrder._id))
			.catch(err => console.log(err))
		return () => {
			isFinished = true
		}
	}, [items, freight, subtotal, tax, total, shipping, billing]) // eslint-disable-line react-hooks/exhaustive-deps
	if (!isBrowser) return <></>

	const getStateCode = state => {
		switch (state) {
			case 'Australian Capital Territory':
				return 'ACT'
			case 'New South Wales':
				return 'NSW'
			case 'Northern Territory':
				return 'NT'
			case 'Queensland':
				return 'QLD'
			case 'South Australia':
				return 'SA'
			case 'Tasmania':
				return 'TAS'
			case 'Victoria':
				return 'VIC'
			case 'Western Australia':
				return 'WA'
			default:
				return ''
				break
		}
	}

	const options = {
		product: products(items),
		mode: 'payment',
		amount: total,
		currency: 'aud',
	};

	const createOrderPayload = (billing, shipping) => {
		return {
			_id: id,
			product: products(items),
			freight: [
				{
					name: isPickUp ? 'Click & Collect' : 'Freight',
					sku: 'FR',
					price: freight,
					quantity: 1,
				},
			],
			fee: [
				{
					name: 'Stripe Fee',
					sku: 'CC',
					quantity: 1,
					price: fee,
				},
			],
			coupon:
				coupon !== null && coupon !== undefined
					? Object.keys(coupon).length != 0
						? [coupon].length > 0
							? [coupon].map(c => ({
								...c,
								discount: [
									c.discount[0].toString(),
									c.discount[1].toString(),
								],
							}))
							: []
						: []
					: [],
			discount: couponTotal,
			subtotal,
			tax,
			total,
			orderTotal,
			...(shipping && { shipping }),
			...(billing && { billing }),
			payment: {
				...(payment.id && { stripeSourceId: payment.id })
			},
		}
	}

	const createAccPayload = (billing, shipping) => {
		const fullName = billing.fullName ?? `${billing.firstName} ${billing.lastName}`;
		return {
			Account: {
				Name: fullName,
				BillingCity: `${billing.address.city}`,
				BillingCountryCode: 'AU',
				BillingStateCode: getStateCode(billing.address.state),
				BillingStreet: `${billing.address.fullAddress.split(',')[0]
					}`,
				BillingPostalCode: `${billing.address.postalCode}`,
			},
			Contact: {
				FirstName: shipping.firstName,
				LastName: shipping.lastName,
				Email: shipping.email,
				Phone: shipping.phone,
				Opt_In_Knowledge__C: optIn ? optIn : '',
				Opt_In_Promotions__C: optIn ? optIn : '',
				MailingCity: shipping.address.city,
				MailingCountryCode: 'AU',
				MailingStateCode: getStateCode(shipping.address.state),
				MailingStreet: `${shipping.address.fullAddress.split(',')[0]
					}`,
				MailingPostalCode: shipping.address.postalCode,
			},
		}
	}

	const placeOrder = async () => {
		setIsLoading(true)

		const variables = createOrderPayload(billing, shipping);
		const accVariables = createAccPayload(billing, shipping);

		const url = '/.netlify/functions/graphql'
		const options = {
			headers: {
				'Content-Type': 'application/json',
			},
		}

		// comment out when testing the new endpoint
		try {
			const {
				data: { data: result, errors },
			} = await axios.post(
				url,
				{
					query: `mutation payOrder(
		$_id: ID!
		$product: [OrderItemInput]
		$freight: [OrderItemInput]
		$coupon: [CouponInput]
		$fee: [OrderItemInput]
		$discount: Int
		$subtotal: Int
		$tax: Int
		$total: Int
		$orderTotal: Int
		$shipping: OrderAddressInput
		$billing: OrderAddressInput
		$payment: PaymentInput!
		) {
		payOrder(
			_id: $_id
			product: $product
			freight: $freight
			coupon: $coupon
			fee: $fee
			discount: $discount
			subtotal: $subtotal
			tax: $tax
			total: $total
			orderTotal: $orderTotal
			shipping: $shipping
			billing: $billing
			payment: $payment
		) {
			_id
			orderId
			payment {
				stripeChargeId
			}
		}
		}`,
					variables,
				},
				options
			)

			console.log(JSON.stringify(result, undefined, 2))
			if (errors) throw errors
			if (!result.payOrder.payment.stripeChargeId)
				throw new Error('payment failed')
			console.log(JSON.stringify(result, undefined, 2))
			if (result.payOrder.payment.stripeChargeId) {
				// comment upto here when testing the new endpoint

				// don't comment this when testing endpoint
				try {
					const datas = {
						grant_type: 'client_credentials',
						client_id: process.env.GATSBY_CLIENT_ID,
						client_secret: process.env.GATSBY_CLIENT_SECRET,
					}

					const {
						data: { access_token },
					} = await axios.post(
						'https://apim.workato.com/oauth2/token',
						QueryString.stringify(datas)
					)

					const config = {
						headers: { Authorization: `Bearer ${access_token}` },
					}

					try {
						const { data } = await axios.post(
							'https://apim.workato.com/maytronics/au-e-comm-create-account-contact-in-sf-v1/createaccountacontactsf',
							accVariables,
							config
						)

						console.log(data)
					} catch (error) {
						const message =
							(error && error[0] && error[0].message) || error
						console.error(JSON.stringify(error, undefined, 2))
					}
				} catch (error) {
					const message =
						(error && error[0] && error[0].message) || error
					console.error(JSON.stringify(error, undefined, 2))
				}
				// don't comment upto here when testing new endpoint

				// comment out when testing the new endpoint
			}

			setIsLoading(false)
			return navigate(
				`/shop/checkout/complete/?order=complete&order_id=${id}`
			)
		} catch (error) {
			const message =
				(error && error[0] && error[0].message) || error
			alert(`Error! ${message}`)
			console.error(JSON.stringify(error, undefined, 2))
			setIsLoading(false)
		}
		// comment upto here when testing the new endpoint
		setIsLoading(false)
	}

	const isConfirmDisabled = () => {
		return ((total <= 0) || Object.keys(shipping).length === 0 && !isLoading);
	}

	return (
		<Elements stripe={stripe} options={total !== 0 ? options : undefined}>
			<Align>
				<StepWrapper>
					<ItemWrapper id={'shipping'}>
						<Item css={tw`pb-0`}>
							<Content>
								<Heading2>
									(1) {isPickUp ? 'Billing' : 'Shipping'} Address
								</Heading2>
							</Content>
						</Item>
						{shipping ? (
							<Item hidden={isShippingOpen}>
								<Content>
									<div css={tw`flex-shrink-0`} />
									<div className="shipping-details">
										<div>
											<div
												css={tw`text-base leading-5 font-medium text-gray-800 truncate`}
											>
												{shipping.firstName} {shipping.lastName}
											</div>
											<div
												css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
											>
												<span css={tw`truncate`}>{shipping.email}</span>
											</div>
											{shipping.phone && (
												<div
													css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
												>
													<span css={tw`truncate`}>{shipping.phone}</span>
												</div>
											)}
										</div>
										<div
											className="shipping-address"
											tw="text-base leading-5 font-medium text-gray-800 m-0! lg:m-auto!"
										>
											{shipping.address?.fullAddress}
										</div>
									</div>
								</Content>
							</Item>
						) : (
							<Item hidden={isShippingOpen}>
								<Content>
									{isPickUp ? 'Who' : 'Where'} do you want it
									{isPickUp ? 'Billied' : 'Shipped'} to?
								</Content>
							</Item>
						)}
						<Item hidden={!isShippingOpen}>
							<Content>
								<ShippingElement hidden={!isShippingOpen}>
									<CheckoutShipping
										setIsShippingOpen={setIsShippingOpen}
										setShipping={setShipping}
										shipping={shipping}
										{...props}
									/>
								</ShippingElement>
							</Content>
						</Item>
						<Item css={tw`pt-0`}>
							<Content>
								<Button
									onClick={() => setIsShippingOpen(!isShippingOpen)}
									hidden={isShippingOpen}
								>
									{shipping
										? `Edit ${isPickUp ? 'Billing' : 'Shipping'}`
										: `Add ${isPickUp ? 'Billing' : 'Shipping'}`}
								</Button>
							</Content>
						</Item>
					</ItemWrapper>
					<ItemWrapper id={'order'}>
						<Item css={tw`pb-0`}>
							<Content>
								<Heading2>(2) Order Review</Heading2>
							</Content>
						</Item>
					</ItemWrapper>
					{total > 1 ? (
						<ul>
							{items.map((item, index) => {
								const { img, name, price, sku, quantity } = item
								return (
									<li key={name}>
										<ItemWrapper>
											<Item>
												<Content>
													<div css={tw`flex-shrink-0`}>
														<img
															css={tw`w-24 h-24 rounded-md bg-gray-200`}
															src={`${img.split('?')[0]
																}?fm=png&fl=png8&fit=pad&w=256&h=256`}
															alt={name}
														/>
													</div>
													<div className="product-details">
														<div>
															<div
																css={tw`text-base leading-5 font-medium text-gray-800 truncate`}
															>
																{name}
															</div>
															<div
																css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
															>
																<span css={tw`truncate`}>{sku}</span>
															</div>
															<Price
																css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
															>
																{Number(price / 100).toLocaleString(
																	props.locale,
																	{
																		style: 'currency',
																		currency: props.currency,
																	}
																)}
															</Price>
														</div>
														<div className="product-selectors">
															<div css={tw`flex flex-col justify-end`}>
																<Price
																	css={tw`text-base leading-none text-gray-600`}
																>
																	{Number(
																		(price / 100) * quantity
																	).toLocaleString(props.locale, {
																		style: 'currency',
																		currency: props.currency,
																	})}
																</Price>
																{!confirm && (
																	<div
																		css={tw`text-xs leading-none text-gray-600 mt-4 ml-auto`}
																	>
																		{isInput[index] ? (
																			<div css={tw`inline-flex items-center`}>
																				<input
																					type="text"
																					value={quantity}
																					onChange={event =>
																						event.target.value >= 1 &&
																						updateQuantity(
																							index,
																							event.target.value
																						)
																					}
																					css={tw`w-8 border border-gray-600 rounded bg-gray-100 py-1 px-1 appearance-none`}
																				/>
																				<div css={tw`flex flex-col`}>
																					<button
																						type="button"
																						onClick={() =>
																							incrementInCart(index)
																						}
																						css={tw`flex justify-center items-center w-4 h-3 border border-gray-600 rounded bg-gray-100 ml-1 mb-px appearance-none`}
																					>
																						<ChevronUp
																							size={10}
																							css={{ strokeWidth: '3px' }}
																							aria-hidden
																						/>
																					</button>
																					<button
																						type="button"
																						onClick={() =>
																							quantity >= 2 &&
																							decrementInCart(index)
																						}
																						css={tw`flex justify-center items-center w-4 h-3 border border-gray-600 rounded bg-gray-100 ml-1 appearance-none`}
																					>
																						<ChevronDown
																							size={10}
																							css={{ strokeWidth: '3px' }}
																							aria-hidden
																						/>
																					</button>
																				</div>
																			</div>
																		) : (
																			<Menu>
																				<MenuButton className="quantity-button">
																					Qty:
																					<span aria-hidden>{quantity}</span>
																					<ChevronDown
																						size={16}
																						css={{ strokeWidth: '2px' }}
																						aria-hidden
																					/>
																				</MenuButton>
																				<MenuList className="quantity-options">
																					<MenuItem
																						onSelect={() =>
																							removeFromCart(item)
																						}
																					>
																						0 (Remove)
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 1)
																						}
																					>
																						1
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 2)
																						}
																					>
																						2
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 3)
																						}
																					>
																						3
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 4)
																						}
																					>
																						4
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 5)
																						}
																					>
																						5
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 6)
																						}
																					>
																						6
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 7)
																						}
																					>
																						7
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 8)
																						}
																					>
																						8
																					</MenuItem>
																					<MenuItem
																						onSelect={() =>
																							updateQuantity(index, 9)
																						}
																					>
																						9
																					</MenuItem>
																					<hr
																						css={tw`border-0 border-t border-gray-600`}
																					/>
																					<MenuItem
																						onSelect={() => showInput(index)}
																					>
																						10+
																					</MenuItem>
																				</MenuList>
																			</Menu>
																		)}
																	</div>
																)}
																{!confirm && (
																	<div
																		css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500 ml-auto`}
																	>
																		<button
																			css={tw`flex text-xs text-blue-600`}
																			onClick={() => removeFromCart(item)}
																			onKeyDown={() => removeFromCart(item)}
																		>
																			Remove
																		</button>
																	</div>
																)}
															</div>
														</div>
													</div>
												</Content>
											</Item>
										</ItemWrapper>
									</li>
								)
							})}
						</ul>
					) : (
						<EmptyCart>
							<EmptyMessage>
								<span role="img" aria-labelledby="cobweb">
									🕸
								</span>
								<span>
									&nbsp; Things are looking a little empty in here &nbsp;
								</span>
								<span role="img" aria-labelledby="package">
									📦
								</span>
							</EmptyMessage>
							<EmptyButton to={'/' + `shop`}>Add Products</EmptyButton>
						</EmptyCart>
					)}
					{/** COUPON CODES */}
					{/* {total > 1 && !confirm && (
						<ItemWrapper id="coupon">
							<hr tw="border-0 border-t border-gray-300 w-full m-0" />
							<Item>
								<Content>
									<form tw="inline-flex" onSubmit={handleOnSubmitCouponInput}>
										<input
											tw="py-1 px-3 mr-2 border-2 border-gray-300 rounded"
											type="text"
											pattern="(^[OZoz]{2}[0-9\s-]{6,13}$)|(^100[OFFXoofx]{4}30$)"
											placeholder="COUPON-CODE"
											name="coupon"
											id="coupon"
											value={couponInput.coupon}
											onChange={handleOnChangeCouponInput}
										/>
										<Button submit>Add Coupon</Button>
									</form>
								</Content>
							</Item>
						</ItemWrapper>
					)} */}

					{/** CLICK & COLLECT */}
					{total > 1 && isPickUp && (
						<ItemWrapper id="coupon">
							<hr tw="border-0 border-t border-gray-300 w-full m-0" />
							<Item>
								<Content>
									( ! ) An item in the cart is Click & Collect, you will need to
									pick up your items from a local dealer stipulated on the final
									invoice.
								</Content>
							</Item>
						</ItemWrapper>
					)}

					<ItemWrapper id="charges">
						<hr tw="border-0 border-t border-gray-300 w-full m-0" />
						<Item>
							<Content>
								<Charges hidden={total < 1}>
									<div>
										{/* {coupon && coupon.name && (
											<ChargeItem disabled={confirm}>
												<ChargeTitle>{coupon.name}</ChargeTitle>
												<ChargeCost>
													{Number(-couponTotal / 100).toLocaleString('en-AU', {
														style: 'currency',
														currency: 'AUD',
													})}{' '}
													AUD
												</ChargeCost>
											</ChargeItem>
										)} */}
										<ChargeItem disabled={confirm}>
											<ChargeTitle>Subtotal</ChargeTitle>
											<ChargeCost>
												{Number(subtotal / 100).toLocaleString(props.locale, {
													style: 'currency',
													currency: props.currency,
												})}{' '}
												{props.locale === 'en-AU'
													? 'AUD'
													: props.locale === 'en-NZ'
														? 'NZD'
														: 'AUD'}
											</ChargeCost>
										</ChargeItem>
										<ChargeItem disabled={confirm}>
											<ChargeTitle>Shipping</ChargeTitle>
											<ChargeCost>
												{Number(freight / 1.1 / 100).toLocaleString(
													props.locale,
													{
														style: 'currency',
														currency: props.currency,
													}
												)}{' '}
												{props.locale === 'en-AU'
													? 'AUD'
													: props.locale === 'en-NZ'
														? 'NZD'
														: 'AUD'}
											</ChargeCost>
										</ChargeItem>
										<ChargeItem disabled={confirm}>
											<ChargeTitle>Tax[GST]</ChargeTitle>
											<ChargeCost>
												{Number(tax / 100).toLocaleString(props.locale, {
													style: 'currency',
													currency: props.currency,
												})}{' '}
												{props.locale === 'en-AU'
													? 'AUD'
													: props.locale === 'en-NZ'
														? 'NZD'
														: 'AUD'}
											</ChargeCost>
										</ChargeItem>
										<ChargeItem tw="text-gray-900 font-medium">
											<ChargeTitle>Total</ChargeTitle>
											<ChargeCost>
												{Number(total / 100).toLocaleString(props.locale, {
													style: 'currency',
													currency: props.currency,
												})}{' '}
												{props.locale === 'en-AU'
													? 'AUD'
													: props.locale === 'en-NZ'
														? 'NZD'
														: 'AUD'}
											</ChargeCost>
										</ChargeItem>
									</div>
								</Charges>
							</Content>
						</Item>
						<Item tw="pt-0">
							<Content>
								<Button
									disabled={isConfirmDisabled()}
									onClick={() => setConfirm(!confirm)}
								>
									{confirm ? `Edit Order` : `Confirm Order`}
								</Button>
							</Content>
						</Item>
						<Item tw="pt-0">
							<div>
								<input
									checked={optIn}
									type="checkbox"
									onClick={() => setOptIn(!optIn)}
								/>
								<label css={tw`text-sm leading-5 text-gray-500 ml-2`}>
									I wish to receive updates and promotions from Maytronics
								</label>
							</div>
						</Item>
					</ItemWrapper>
				</StepWrapper>
				<SummeryWrapper>
					<Summery id={'summery'}>
						<div css={tw`w-full mb-4`}>
							<span>(3) Payment</span>
						</div>
						<div css={[!confirm ? tw`hidden` : tw`w-full`]}>
							<ItemWrapper id={'payment'}>
								{payment.card && (
									<Item>
										<Content>
											<div className="shipping-details">
												<Content>
													<CreditCard>
														<div css={{ display: 'flex' }}>
															<figure>
																<EMVChip stroke={'20'} />
															</figure>
															<EMVContactless stroke={'50'} />
														</div>
														<span
															css={
																payment.card.brand === 'amex' && {
																	padding: '0 2.6rem',
																}
															}
														>
															{payment.card.brand === 'amex'
																? '•••• •••••• •'
																: '•••• •••• •••• '}
															<i>{payment.card.last4}</i>
														</span>
														<div css={{ display: 'flex' }}>
															<small></small>
															<small></small>
															{payment.card.brand === 'Visa' ? <Visa /> : ''}
															{payment.card.brand === 'Amex' ? <Amex /> : ''}
															{payment.card.brand === 'Mastercard' ? (
																<MasterCard />
															) : (
																''
															)}
														</div>
													</CreditCard>
												</Content>
												<div
													className="shipping-address"
													tw="text-base leading-5 font-medium text-gray-800 m-0! lg:m-auto!"
												>
													<div tw="text-base leading-5 font-medium text-gray-800 truncate">
														{billing.firstName} {billing.lastName}
													</div>
													<div
														css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
													>
														<span css={tw`truncate`}>{billing.email}</span>
													</div>
													{billing.phone && (
														<div
															css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
														>
															<span css={tw`truncate`}>{billing.phone}</span>
														</div>
													)}
													<div css={tw`mt-2`}>{billing.address?.fullAddress}</div>
												</div>
											</div>
										</Content>
									</Item>
								)}
								<Item hidden={!isPaymentOpen}>
									<Content>
										<ShippingElement hidden={!isPaymentOpen}>
											<CheckoutPayment
												isPickUp={isPickUp}
												sameAsShipping={sameAsShipping}
												setSameAsShipping={setSameAsShipping}
												setIsPaymentOpen={setIsPaymentOpen}
												setPayment={setPayment}
												payment={payment}
												setBilling={setBilling}
												billing={billing}
												shipping={shipping}
												{...props}
											/>
										</ShippingElement>
									</Content>
								</Item>
								<Item css={tw`py-0 !px-0`}>
									<Content>
										<Button
											onClick={() => setIsPaymentOpen(!isPaymentOpen)}
											hidden={isPaymentOpen}
											disabled={!shipping.firstName}
											css={[tw`justify-center`, !payment.card && tw`w-full`]}
										>
											{billing ? payment.card ? 'Edit Card' : `Pay by Card` : `Add Card`}
										</Button>
									</Content>
								</Item>
							</ItemWrapper>
							{payment.card && <PlaceOrder
								type="button"
								disabled={!payment.card || isLoading}
								onClick={placeOrder}
								css={tw`mt-4`}
							>
								{isLoading ? (
									<>
										{/* <Spinner /> */}
										<span>Placing Order</span>
									</>
								) : (
									<span>Place Order</span>
								)}
							</PlaceOrder>}

							<CheckoutExpress
								show={!isPaymentOpen && !payment.card}
								setIsLoading={setIsLoading}
								orderID={id}
								orderInfo={options}
								createAccPayload={createAccPayload}
								createOrderPayload={createOrderPayload}
								shipping={shipping}
							/>
						</div>
						<div css={tw`flex items-center justify-between px-4 py-4 w-full`}>
							<span css={tw`inline align-middle`}>
								Items
								<span css={tw`inline-block text-sm pl-1`}>({count})</span>
							</span>
							<span>
								{Number(total / 100).toLocaleString(props.locale, {
									style: 'currency',
									currency: props.currency,
								})}
							</span>
						</div>
						<hr css={tw`border-0 border-t border-gray-300 w-full my-1`} />
						<div css={tw`flex items-center justify-between px-4 py-4 w-full`}>
							<span css={tw`text-xl`}>Total</span>
							<span css={tw`text-xl`}>
								{Number(total / 100).toLocaleString(props.locale, {
									style: 'currency',
									currency: props.currency,
								})}
							</span>
						</div>
					</Summery>
				</SummeryWrapper>
			</Align>
		</Elements>
	)
}

export default CheckoutForm
